import type { BusinessDate } from '../date/business-date';
import type { IsoWeek } from '../date/iso-week';
import { Hours } from '../shared/hours';
import type { ExecutionResourceId } from '../shared/ids';
import type { CapacityQuarterHours, ResourceCapacityIndex } from './resource-capacity';

/**
 * Disponível, planejado e saldo (RN-043).
 *
 * O núcleo de cálculo da fase, e o motivo de ele ser função pura: **a tela e o caso de
 * uso chamam exatamente este código**. Não existe uma segunda conta em React que avisa
 * "vai passar da capacidade" enquanto o caso de uso decide por outra — as duas divergiriam
 * na primeira semana com feriado, e o usuário aprenderia a não confiar em nenhuma das
 * duas.
 *
 * Tudo em quartos de hora inteiros (ADR-106): saldo nunca é dízima.
 */

export type CapacityStatus = 'UNLIMITED' | 'WITHIN' | 'AT_LIMIT' | 'EXCEEDED';

export const CAPACITY_STATUS_LABEL: Readonly<Record<CapacityStatus, string>> = {
  UNLIMITED: 'Sem limite',
  WITHIN: 'Dentro da capacidade',
  AT_LIMIT: 'No limite',
  EXCEEDED: 'Capacidade excedida',
};

export type CapacityScope = 'DAY' | 'WEEK' | 'MONTH' | 'PERIOD';

export interface CapacityBalance {
  readonly resourceId: ExecutionResourceId;
  readonly scope: CapacityScope;
  readonly from: string;
  readonly to: string;
  /** Ausente = sem limite declarado (RN-047). */
  readonly availableQuarterHours?: number;
  readonly plannedQuarterHours: number;
  /** Ausente quando sem limite. Negativo = sobrecarga, e isso é informação legítima. */
  readonly balanceQuarterHours?: number;
  readonly status: CapacityStatus;
}

/**
 * Horas já comprometidas em um intervalo de dias.
 *
 * A alocação semanal cobre a semana inteira; a diária (Fase 8) cobre um dia. As duas
 * viram o mesmo `PlannedSpan`, e é por isso que o mesmo cálculo serve às duas fases.
 */
export interface PlannedSpan {
  readonly resourceId: ExecutionResourceId;
  readonly from: string;
  readonly to: string;
  readonly quarterHours: number;
}

/**
 * Soma o que está planejado **dentro** do intervalo perguntado.
 *
 * Contido, não sobreposto: cada compromisso cobre um dia, e um dia ou está dentro do
 * intervalo perguntado ou não está. A mesma função responde o dia (intervalo de um dia
 * só) e a semana (intervalo de sete), sem nenhum rateio pelo caminho — rateio produziria
 * um número plausível e errado, que é o pior tipo de número.
 */
export function plannedQuarterHoursIn(
  spans: readonly PlannedSpan[],
  resourceId: ExecutionResourceId,
  from: string,
  to: string,
): number {
  let total = 0;
  for (const span of spans) {
    if (span.resourceId !== resourceId) continue;
    if (span.from >= from && span.to <= to) total += span.quarterHours;
  }
  return total;
}

/**
 * O total passou da jornada? A conta única da **hora extra** (RN-292).
 *
 * É a pergunta que o planejamento, a conferência e o RDO fazem — e que a tela faz antes de
 * marcar a justificativa como obrigatória. Existir uma só é o que impede o campo de
 * aparecer obrigatório numa tela e o caso de uso aceitar sem motivo na outra.
 *
 * `total` é tudo o que a pessoa tem **no dia**, somando todas as frentes e serviços;
 * `capacity` é a jornada daquele dia pelo calendário — zero no sábado e no feriado, onde
 * qualquer hora é extra —, ou a capacidade diária declarada da equipe terceirizada.
 * `undefined` é "sem limite" (RN-047): nunca é hora extra.
 */
export function isOvertime(total: number, capacity: CapacityQuarterHours): boolean {
  return capacity !== undefined && total > capacity;
}

export function capacityStatusOf(available: CapacityQuarterHours, planned: number): CapacityStatus {
  if (available === undefined) return 'UNLIMITED';
  if (isOvertime(planned, available)) return 'EXCEEDED';
  if (planned === available) return 'AT_LIMIT';
  return 'WITHIN';
}

/** `45h / 45h` · `32h / 45h` · `48h / 45h`. O texto que acompanha a cor (§4.4). */
export function formatBalance(balance: CapacityBalance): string {
  const planned = Hours.ofQuarters(balance.plannedQuarterHours).format();
  return balance.availableQuarterHours === undefined
    ? `${planned} · sem limite`
    : `${planned} / ${Hours.ofQuarters(balance.availableQuarterHours).format()}`;
}

/**
 * Capacidade de um recurso por dia, semana e mês.
 *
 * Recebe o índice de capacidade e os compromissos já carregados; não lê banco e não
 * conhece obra. Testável com uma tabela de casos, reutilizável pelo planejamento
 * semanal, pelo diário e pela conferência.
 */
export class CapacityCalculator {
  constructor(
    private readonly index: ResourceCapacityIndex,
    private readonly spans: readonly PlannedSpan[] = [],
  ) {}

  forPeriod(
    resourceId: ExecutionResourceId,
    from: BusinessDate,
    to: BusinessDate,
    scope: CapacityScope = 'PERIOD',
  ): CapacityBalance {
    const available = this.index.between(resourceId, from, to);
    const planned = plannedQuarterHoursIn(this.spans, resourceId, from.toString(), to.toString());

    return {
      resourceId,
      scope,
      from: from.toString(),
      to: to.toString(),
      plannedQuarterHours: planned,
      status: capacityStatusOf(available, planned),
      ...(available === undefined
        ? {}
        : { availableQuarterHours: available, balanceQuarterHours: available - planned }),
    };
  }

  forDay(resourceId: ExecutionResourceId, date: BusinessDate): CapacityBalance {
    return this.forPeriod(resourceId, date, date, 'DAY');
  }

  forWeek(resourceId: ExecutionResourceId, week: IsoWeek): CapacityBalance {
    return this.forPeriod(resourceId, week.startDate(), week.endDate(), 'WEEK');
  }

  /**
   * Mês civil da data de referência.
   *
   * O que conta é o que está planejado **inteiramente dentro** do mês; uma semana a
   * cavalo entre agosto e setembro pertence ao mês em que couber por completo, e a
   * regra vale igual para os dois meses — nenhuma hora é contada duas vezes.
   */
  forMonth(resourceId: ExecutionResourceId, reference: BusinessDate): CapacityBalance {
    return this.forPeriod(resourceId, reference.startOfMonth(), reference.endOfMonth(), 'MONTH');
  }

  /** Saldo do dia, da semana e do mês de uma vez — o resumo que a tela exibe. */
  summarize(
    resourceId: ExecutionResourceId,
    week: IsoWeek,
    reference: BusinessDate,
  ): { day: CapacityBalance; week: CapacityBalance; month: CapacityBalance } {
    return {
      day: this.forDay(resourceId, reference),
      week: this.forWeek(resourceId, week),
      month: this.forMonth(resourceId, reference),
    };
  }
}

/**
 * Converte alocações em compromissos — a ponte entre entidade e cálculo.
 *
 * Cada alocação cobre **um dia**, então o intervalo do compromisso é a própria data. É o
 * que faz o mesmo cálculo responder tanto "quanto está planejado nesta segunda" quanto
 * "quanto está planejado nesta semana", sem nenhuma conversão pelo caminho.
 */
export function spansFromAllocations(
  allocations: readonly {
    resourceId: ExecutionResourceId;
    date: string;
    quarterHours: number;
    deletedAt?: string;
  }[],
): PlannedSpan[] {
  return (
    allocations
      // Alocação excluída não conta (RN-049) — a exclusão some do saldo, não do histórico.
      .filter((allocation) => allocation.deletedAt === undefined)
      .map((allocation) => ({
        resourceId: allocation.resourceId,
        from: allocation.date,
        to: allocation.date,
        quarterHours: allocation.quarterHours,
      }))
  );
}
